class TBCUgcSlider {
  constructor(element) {
    this.element = element;
    this.swiper = null;

    /* Marquee state */
    this.rafId = null;
    this.translate = 0;
    this.loopWidth = 0; // px width of ONE full set of original slides
    this.speedPxPerMs = 0;
    this.isPaused = false;
    this.isInteracting = false;
    this.lastTimestamp = null;

    this.onFrame = this.onFrame.bind(this);

    this.init();
  }

  getBoolean(value) {
    return value === 'true';
  }

  /*
   * Duplicate the slide set so there is always a second,
   * identical run of slides to scroll into. We only need
   * ONE extra copy because we reset translate by exactly
   * one loopWidth before the seam is ever visible.
   */
  duplicateSlides(wrapperEl) {
    const originalSlides = Array.from(
      wrapperEl.querySelectorAll(':scope > .swiper-slide')
    );

    if (wrapperEl.dataset.tbcDuplicated === 'true') {
      return originalSlides.length;
    }

    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      wrapperEl.appendChild(clone);
    });

    wrapperEl.dataset.tbcDuplicated = 'true';

    return originalSlides.length;
  }

  measureLoopWidth(originalSlideCount) {
    const slides = this.swiper.slides;
    const spaceBetween = this.swiper.params.spaceBetween || 0;

    let width = 0;
    for (let i = 0; i < originalSlideCount; i++) {
      width += slides[i].offsetWidth + spaceBetween;
    }

    this.loopWidth = width;
  }

  init() {
    if (!this.element || typeof Swiper === 'undefined') return;

    /* Prevent duplicate Swiper instances in Theme Editor */
    if (this.element.swiper) {
      this.element.swiper.destroy(true, true);
    }
    this.stopMarquee();

    const desktopSlides = parseFloat(this.element.dataset.desktopSlides) || 5.5;
    const tabletSlides = parseFloat(this.element.dataset.tabletSlides) || 3.5;
    const mobileSlides = parseFloat(this.element.dataset.mobileSlides) || 2.2;

    const desktopSpace = parseInt(this.element.dataset.desktopSpace, 10) || 0;
    const mobileSpace = parseInt(this.element.dataset.mobileSpace, 10) || 0;

    const loop = this.getBoolean(this.element.dataset.loop);
    const autoplayEnabled = this.getBoolean(this.element.dataset.autoplay);
    const freeMode = this.getBoolean(this.element.dataset.freeMode);
    const pauseOnHover = this.getBoolean(this.element.dataset.pauseHover);
    const direction = this.element.dataset.direction === 'right' ? 'right' : 'left';

    /* marquee-speed is px/second, matches the "Marquee Speed" range setting */
    const marqueeSpeedPxPerSecond =
      parseInt(this.element.dataset.marqueeSpeed, 10) || 35;

    this.speedPxPerMs = marqueeSpeedPxPerSecond / 1000;
    this.pauseOnHover = pauseOnHover;
    this.direction = direction;

    const prevButton = this.element.querySelector('.tbc-ugc__prev');
    const nextButton = this.element.querySelector('.tbc-ugc__next');
    const pagination = this.element.querySelector('.tbc-ugc__pagination');

    const wrapperEl = this.element.querySelector('.swiper-wrapper');

    /* Only duplicate/marquee when autoplay is actually requested */
    let originalSlideCount = 0;
    if (autoplayEnabled && wrapperEl) {
      originalSlideCount = this.duplicateSlides(wrapperEl);
    }

    this.swiper = new Swiper(this.element, {
      slidesPerView: mobileSlides,
      spaceBetween: mobileSpace,

      // Marquee mode drives its own translate (no Swiper loop -> no
      // loop-fix jump). When autoplay is off there's no continuous
      // motion, so a normal Swiper loop is fine for nav/pagination.
      loop: autoplayEnabled ? false : loop,
      watchSlidesProgress: true,
      centeredSlides: false,

      allowTouchMove: true,
      grabCursor: true,

      freeMode: freeMode
        ? { enabled: true, momentum: true }
        : false,

      autoplay: false, // we drive the marquee ourselves

      navigation:
        prevButton && nextButton
          ? { prevEl: prevButton, nextEl: nextButton }
          : false,

      pagination: pagination ? { el: pagination, clickable: true } : false,

      breakpoints: {
        750: { slidesPerView: tabletSlides, spaceBetween: desktopSpace },
        990: { slidesPerView: desktopSlides, spaceBetween: desktopSpace }
      },

      on: {
        touchStart: () => {
          this.isInteracting = true;
        },
        touchEnd: () => {
          this.isInteracting = false;
          // resync our translate tracker with wherever the user's drag left it
          this.translate = this.swiper.translate;
        },
        resize: () => {
          if (originalSlideCount) this.measureLoopWidth(originalSlideCount);
        }
      }
    });

    if (autoplayEnabled && originalSlideCount) {
      // wait one frame so Swiper has laid out slide widths
      requestAnimationFrame(() => {
        this.measureLoopWidth(originalSlideCount);
        // "right" plays the same append-only duplicate set in reverse:
        // start at -loopWidth (visually identical to translate 0,
        // since the clone is pixel-identical) and count up to 0.
        this.translate =
          this.direction === 'right' ? -this.loopWidth : this.swiper.translate;
        this.startMarquee();
      });

      if (pauseOnHover) {
        this.element.addEventListener('mouseenter', () => {
          this.isPaused = true;
        });
        this.element.addEventListener('mouseleave', () => {
          this.isPaused = false;
        });
      }
    }
  }

  startMarquee() {
    this.stopMarquee();
    this.lastTimestamp = null;
    this.rafId = requestAnimationFrame(this.onFrame);
  }

  stopMarquee() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  onFrame(timestamp) {
    if (this.lastTimestamp === null) {
      this.lastTimestamp = timestamp;
    }

    const delta = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    if (!this.isPaused && !this.isInteracting && this.loopWidth > 0) {
      const step = delta * this.speedPxPerMs;

      if (this.direction === 'right') {
        this.translate += step;

        // Seamless wrap: once we've counted back up to 0, snap to
        // -loopWidth. Both positions look identical (clone = original).
        if (this.translate >= 0) {
          this.translate -= this.loopWidth;
        }
      } else {
        this.translate -= step;

        // Seamless wrap: once we've scrolled a full set of original
        // slides, snap back by exactly that width. Since the second
        // set is an identical clone, this is visually imperceptible.
        if (this.translate <= -this.loopWidth) {
          this.translate += this.loopWidth;
        }
      }

      this.swiper.setTranslate(this.translate);
      this.swiper.updateProgress();
      this.swiper.updateSlidesClasses();
    }

    this.rafId = requestAnimationFrame(this.onFrame);
  }

  destroy() {
    this.stopMarquee();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

function initTBCUgcSliders(container = document) {
  container.querySelectorAll('[data-tbc-ugc-swiper]').forEach((slider) => {
    if (slider.dataset.tbcInitialized === 'true') {
      return;
    }
    slider.dataset.tbcInitialized = 'true';
    slider.tbcInstance = new TBCUgcSlider(slider);
  });
}

function waitForTBCSwiper() {
  if (typeof Swiper !== 'undefined') {
    initTBCUgcSliders();
    return;
  }
  setTimeout(waitForTBCSwiper, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForTBCSwiper);
} else {
  waitForTBCSwiper();
}

/*
 * Shopify Theme Editor
 */
document.addEventListener('shopify:section:load', (event) => {
  const slider = event.target.querySelector('[data-tbc-ugc-swiper]');
  if (!slider) return;

  if (slider.tbcInstance) {
    slider.tbcInstance.destroy();
  }

  slider.dataset.tbcInitialized = 'false';
  slider.dataset.tbcDuplicated = 'false';

  // remove any previously injected clones so the section can be
  // re-edited/re-rendered cleanly by the theme editor
  const wrapperEl = slider.querySelector('.swiper-wrapper');
  if (wrapperEl) {
    wrapperEl
      .querySelectorAll(':scope > .swiper-slide[aria-hidden="true"]')
      .forEach((clone) => clone.remove());
  }

  initTBCUgcSliders(event.target);
});